import React, { useEffect, useState } from 'react';
import moment from 'moment/moment';
import { NCZIEntry } from '../../../Interfaces/ncziTypes';
import useTranslation from '../../../../../Hooks/useTranslation';
import { joinClasses } from '../../../../../Utils/className.util';
import Tag from '../../../../../Components/Tag/Tag';
import EHREntryInfo from '../../EHR/EHRExtract/EHREntry/EHREntryInfo';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../Enums/Theme.enum';
import ElectronicDPNChangeModal from '../Change/ElectronicDPNChangeModal';
import Text from '../../../../../Components/Base/Text/Text';
import ElectronicDPNDetailModal from '../Detail/ElectronicDPNDetailModal';
import ElectronicDPNHistoryModal from '../History/ElectronicDPNHistoryModal';

// @ts-ignore
import styles from './ElectronicDPNListItem.module.scss';

const AT_PN_DATE_FROM =
	'CEN-EN13606-ENTRY.PotvrdenieDocasnejPraceneschopnosti.v1/at0002';
const AT_PN_DATE_TO =
	'CEN-EN13606-ENTRY.PotvrdenieDocasnejPraceneschopnosti.v1/at0003';

interface Props {
	item: NCZIEntry;
	patientIdentifier: string;
	onCloseDetail?: (needReload: boolean) => void;
	history: boolean;
	readonly: boolean;
	disableCreate?: boolean;
}

const ElectronicDPNListItem: React.FC<Props> = props => {
	const {
		item,
		patientIdentifier,
		onCloseDetail,
		history = false,
		readonly = false,
		disableCreate = false,
	} = props;
	const [entryOpened, setEntryOpened] = useState<boolean>(false);
	const [historyOpened, setHistoryOpened] = useState<boolean>(false);
	const [electronicDPNChangeOpen, setElectronicDPNChangeOpen] =
		useState<boolean>(false);
	const [entryTitle, setEntryTitle] = useState<string>(item?.name || '');
	const { t } = useTranslation();
	const [hasDateTo, setHasDateTo] = useState<boolean>(false);

	useEffect(() => {
		let newName = '';
		let dateFrom = undefined;
		let dateTo = undefined;

		for (const entryItem of item.items) {
			if (entryItem.element) {
				if (AT_PN_DATE_FROM === entryItem.element.archetypeId) {
					dateFrom = moment(entryItem.element.value).format('DD.MM.YYYY');
				} else if (AT_PN_DATE_TO === entryItem.element.archetypeId) {
					dateTo = moment(entryItem.element.value).format('DD.MM.YYYY');
					setHasDateTo(true);
				}
			}
		}

		if (dateFrom) {
			if (dateTo) {
				newName = dateFrom + ' - ' + dateTo;
			} else {
				newName = t('nczi.electronicDPN.list.from') + ' ' + dateFrom;
			}
		}

		if (newName) {
			setEntryTitle(newName);
		}
	}, []);

	const handleCloseDetailModal = (needReload: boolean = false) => {
		setEntryOpened(false);
		if (needReload) {
			onCloseDetail?.(needReload);
		}
	};

	const handleCloseElectronicDPNChangeModal = (needReload: boolean = false) => {
		setElectronicDPNChangeOpen(false);
		if (needReload) {
			onCloseDetail?.(needReload);
		}
	};

	const isCancelled = item.info?.version === 'VER04';

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					isCancelled ? styles.cancelled : undefined,
				])}>
				<div className={styles.border}>
					<div className={styles.content}>
						<div className={styles.header}>
							<div className={styles.openEntryName}>
								<Text
									tx={entryTitle}
									className={joinClasses([
										isCancelled ? styles.cancelledTitle : '',
									])}
								/>
								{!hasDateTo && (
									<Tag
										label={'nczi.electronicDPN.withoutEnd'}
										className={joinClasses([
											styles.unclosedTag,
											isCancelled ? styles.cancelledTitle : '',
										])}
									/>
								)}
								{isCancelled && (
									<Tag
										label={'nczi.cancelled'}
										className={styles.cancelledTag}
									/>
								)}
							</div>
						</div>
						<EHREntryInfo info={item.info} />
					</div>
					<div className={styles.controls}>
						<LinkButton
							title={'nczi.showDetails'}
							theme={Theme.BLUE}
							onClick={() => setEntryOpened(true)}
						/>
						{!hasDateTo && !readonly && !isCancelled && (
							<LinkButton
								firstUpper
								title={'nczi.endOrChangeDPN'}
								theme={Theme.RED}
								onClick={() => setElectronicDPNChangeOpen(true)}
							/>
						)}
						{!history && (
							<LinkButton
								firstUpper
								title={'nczi.showHistory'}
								theme={Theme.BLUE}
								onClick={() => setHistoryOpened(true)}
							/>
						)}
					</div>
				</div>
			</div>
			<ElectronicDPNDetailModal
				opened={entryOpened}
				onClose={handleCloseDetailModal}
				electronicConfirmation={item}
				patientIdentifier={patientIdentifier}
				readonly={readonly}
				disableCreate={disableCreate}
			/>
			<ElectronicDPNHistoryModal
				opened={historyOpened}
				onClose={() => {
					setHistoryOpened(false);
				}}
				electronicConfirmation={item}
				patientIdentifier={patientIdentifier}
				disableCreate={disableCreate}
			/>
			<ElectronicDPNChangeModal
				opened={electronicDPNChangeOpen}
				onClose={handleCloseElectronicDPNChangeModal}
				patientIdentifier={patientIdentifier}
				electronicConfirmation={item}
			/>
		</>
	);
};

export default ElectronicDPNListItem;
